import React, { useState } from 'react'
import { Modal, Button, Icon, Alert, Upload, Tabs } from 'antd'
import VoiceUploadDetailsModal from '../VoiceUploadDetailsModal'
import VoiceDetailsModal from './components/VoiceDetailsModal'
import VoiceFilterControls from './components/VoiceFilterControls'
import VoiceLibrarySection from './components/VoiceLibrarySection'
import VoiceListItem from './components/VoiceListItem'
import {
  SORT_DEFAULT,
  getVoiceDisplayName,
  getVoicePreviewUrl,
  sortVoices
} from './utils'
import './VoiceSelectionModal.scss'

const { TabPane } = Tabs

const VoiceSelectionModal = ({
  visible,
  onCancel,
  onSelect,
  voices,
  loadingVoices,
  handlePlayPreview,
  playingVoice,
  audioProgress,
  selectedVoiceId,
  targetName,
  handleUploadVoice,
  uploading,
  uploadProgress,
  uploadError,
  setUploadError,
  beforeUpload,
  fetchVoices,
  pendingUploadFile,
  setPendingUploadFile,
  pendingVoiceName,
  pendingVoiceDescription,
  pendingVoiceLanguage,
  setPendingVoiceName,
  setPendingVoiceDescription,
  setPendingVoiceLanguage,
  onRetryUpload,
  onOverwriteUpload,
  recommendedVoices = [],
  loadingRecommendedVoices = false,
  recommendedVoicesError = null,
  voicesError = null,
  showRecommendedTab = false,
  fetchRecommendedVoices
}) => {
  const [activeTab, setActiveTab] = useState(
    showRecommendedTab ? 'recommended' : 'system'
  )
  const [sortBy, setSortBy] = useState(SORT_DEFAULT)
  const [detailsView, setDetailsView] = useState(null)
  const [isUploadDetailsModalVisible, setUploadDetailsModalVisible] = useState(
    false
  )

  const resolvedTargetName =
    typeof targetName === 'object'
      ? targetName?.name
      : typeof targetName === 'string'
        ? targetName.trim()
        : ''
  const modalTargetName =
    resolvedTargetName || (showRecommendedTab ? 'Narrator' : '')

  const systemVoices = voices.filter(v => v.type !== 'uploaded')
  const customVoices = voices.filter(v => v.type === 'uploaded')

  const sortedRecommendedVoices = sortVoices(recommendedVoices, sortBy)
  const sortedSystemVoices = sortVoices(systemVoices, sortBy)
  const sortedCustomVoices = sortVoices(customVoices, sortBy)

  const hasSystemVoices = systemVoices.length > 0
  const hasCustomVoices = customVoices.length > 0

  const resetFilters = () => {
    setSortBy(SORT_DEFAULT)
  }

  const openUploadDetails = file => {
    if (setPendingUploadFile) {
      setPendingUploadFile(file)
    }
    if (!pendingVoiceName?.trim() && file) {
      setPendingVoiceName(file.name.replace(/\.[^/.]+$/, ''))
    }
    setPendingVoiceDescription('')
    setPendingVoiceLanguage('')
    setUploadDetailsModalVisible(true)
  }

  const closeUploadDetails = () => {
    setUploadDetailsModalVisible(false)
    if (setPendingUploadFile) {
      setPendingUploadFile(null)
    }
    setPendingVoiceName('')
    setPendingVoiceDescription('')
    setPendingVoiceLanguage('')
  }

  const handleFileSelect = file => {
    const fileAllowed = beforeUpload ? beforeUpload(file) : true
    if (fileAllowed !== false) {
      openUploadDetails(file)
    }
    return false
  }

  const handleConfirmUpload = async () => {
    if (!pendingUploadFile) return
    await handleUploadVoice({
      file: pendingUploadFile,
      onSuccess: () => closeUploadDetails(),
      onError: () => { }
    })
  }

  const handleRetryUploadWithClose = async () => {
    if (typeof onRetryUpload !== 'function') return
    await onRetryUpload({ onSuccess: () => closeUploadDetails() })
  }

  const handleOverwriteUploadWithClose = async () => {
    if (typeof onOverwriteUpload !== 'function') return
    await onOverwriteUpload({ onSuccess: () => closeUploadDetails() })
  }

  const renderSortedVoiceList = (sortedVoices, sourceVoices, variant) => (
    <>
      <VoiceFilterControls
        voices={sourceVoices}
        sortBy={sortBy}
        onSortChange={setSortBy}
        onResetAll={resetFilters}
        totalCount={sortedVoices.length}
      />

      {sortedVoices.length > 0 ? (
        <div
          className={`voice-selection-modal__voice-list ${variant === 'custom' ? 'voice-selection-modal__voice-grid' : ''
            }`}
        >
          {sortedVoices.map((voice, index) => {
            const displayName = getVoiceDisplayName(voice, variant, index)

            return (
              <VoiceListItem
                key={voice.id}
                voice={voice}
                variant={variant}
                displayName={displayName}
                isSelected={selectedVoiceId === voice.id}
                isPlaying={playingVoice === voice.id}
                progress={playingVoice === voice.id ? audioProgress : 0}
                onSelect={() => onSelect(voice.id)}
                onViewDetails={() =>
                  setDetailsView({
                    voice,
                    variant,
                    displayName
                  })
                }
                onPlay={() =>
                  handlePlayPreview(voice.id, getVoicePreviewUrl(voice))
                }
              />
            )
          })}
        </div>
      ) : (
        <div className="voice-selection-modal__state-card voice-selection-modal__state-card--empty">
          <Icon type="filter" />
          <p>No voices available for this list.</p>
        </div>
      )}
    </>
  )

  return (
    <Modal
      visible={visible}
      onCancel={onCancel}
      footer={null}
      width={800}
      title={
        modalTargetName ? `Select Voice for ${modalTargetName}` : 'Select Voice'
      }
      centered
      className="voice-selection-modal"
      destroyOnClose
    >
      <div className="voice-selection-modal__content">
        <Tabs activeKey={activeTab} onChange={setActiveTab} animated={false}>
          {showRecommendedTab && (
            <TabPane
              tab={
                <span>
                  <Icon type="star" />
                  Recommended Voices
                </span>
              }
              key="recommended"
            >
              <div className="voice-selection-modal__tab-content">
                <div className="voice-selection-modal__recommendation-hero">
                  <div className="voice-selection-modal__recommendation-copy">
                    <h3>Recommended for your story</h3>
                    <p>
                      Start with the top match, compare a few options, and use
                      the eye icon to inspect each voice before choosing.
                    </p>
                  </div>
                  {typeof fetchRecommendedVoices === 'function' && (
                    <Button
                      type="default"
                      icon="reload"
                      onClick={fetchRecommendedVoices}
                      loading={loadingRecommendedVoices}
                      className="voice-selection-modal__refresh-btn"
                    >
                      Refresh
                    </Button>
                  )}
                </div>

                {recommendedVoicesError && (
                  <Alert
                    message={recommendedVoicesError}
                    type="warning"
                    showIcon
                    className="voice-selection-modal__alert"
                  />
                )}

                {loadingRecommendedVoices ? (
                  <div className="voice-selection-modal__state-card">
                    <div className="processing-visual">
                      <div className="custom-loader" />
                      <div className="loader-center">
                        <div className="inner-dot" />
                      </div>
                    </div>
                    <p>Curating recommended voices...</p>
                  </div>
                ) : recommendedVoices.length > 0 ? (
                  renderSortedVoiceList(
                    sortedRecommendedVoices,
                    recommendedVoices,
                    'recommended'
                  )
                ) : (
                  <div className="voice-selection-modal__state-card voice-selection-modal__state-card--empty">
                    <Icon type="bulb" />
                    <p>No recommendations available for this story yet.</p>
                  </div>
                )}
              </div>
            </TabPane>
          )}

          <TabPane
            tab={
              <span>
                <Icon type="customer-service" />
                System Voices
              </span>
            }
            key="system"
          >
            <div className="voice-selection-modal__tab-content">
              <VoiceLibrarySection
                title="System voices"
                description="Choose from our library of high-quality AI voices with a cleaner, easier comparison layout."
                onRefresh={fetchVoices}
                isRefreshing={loadingVoices}
                error={voicesError}
                isLoading={loadingVoices}
                loadingText="Loading available system voices..."
                emptyIcon="customer-service"
                emptyText="No system voices are available right now."
              >
                {hasSystemVoices &&
                  renderSortedVoiceList(
                    sortedSystemVoices,
                    systemVoices,
                    'system'
                  )}
              </VoiceLibrarySection>
            </div>
          </TabPane>

          <TabPane
            tab={
              <span>
                <Icon type="user" />
                Custom Voices
              </span>
            }
            key="custom"
          >
            <div className="voice-selection-modal__tab-content">
              <VoiceLibrarySection
                title="Custom voices"
                description="Upload your own sample, then compare saved custom voices using the same layout as the library."
                onRefresh={fetchVoices}
                isRefreshing={loadingVoices}
                error={voicesError}
                isLoading={false}
                emptyIcon="user"
                emptyText="No custom voices uploaded yet."
              >
                <div className="voice-selection-modal__upload-section">
                  {uploadError && !isUploadDetailsModalVisible && (
                    <Alert
                      message={uploadError}
                      type="error"
                      showIcon
                      closable
                      onClose={() => setUploadError(null)}
                      className="voice-selection-modal__alert"
                    />
                  )}

                  <Upload.Dragger
                    accept=".mp3"
                    beforeUpload={handleFileSelect}
                    showUploadList={false}
                  >
                    <p className="ant-upload-drag-icon">
                      <Icon type="cloud-upload" />
                    </p>
                    <p className="ant-upload-text">
                      Click or drag audio file to start uploading a custom
                      voice.
                    </p>
                    <p className="ant-upload-hint">
                      Upload an MP3 file first, then add name, description, and
                      an optional language.
                    </p>
                  </Upload.Dragger>

                  <VoiceUploadDetailsModal
                    visible={isUploadDetailsModalVisible}
                    pendingUploadFile={pendingUploadFile}
                    uploadError={uploadError}
                    uploading={uploading}
                    uploadProgress={uploadProgress}
                    pendingVoiceName={pendingVoiceName}
                    pendingVoiceDescription={pendingVoiceDescription}
                    pendingVoiceLanguage={pendingVoiceLanguage}
                    setPendingVoiceName={setPendingVoiceName}
                    setPendingVoiceDescription={setPendingVoiceDescription}
                    setPendingVoiceLanguage={setPendingVoiceLanguage}
                    onCancel={closeUploadDetails}
                    onConfirmUpload={handleConfirmUpload}
                    onRetryUpload={handleRetryUploadWithClose}
                    onOverwriteUpload={handleOverwriteUploadWithClose}
                  />
                </div>

                {loadingVoices ? (
                  <div className="voice-selection-modal__state-card">
                    <div className="processing-visual">
                      <div className="custom-loader" />
                      <div className="loader-center">
                        <div className="inner-dot" />
                      </div>
                    </div>
                    <p>Loading your saved custom voices...</p>
                  </div>
                ) : hasCustomVoices ? (
                  renderSortedVoiceList(
                    sortedCustomVoices,
                    customVoices,
                    'custom'
                  )
                ) : (
                  <div className="voice-selection-modal__state-card voice-selection-modal__state-card--empty">
                    <Icon type="user" />
                    <p>No custom voices uploaded yet.</p>
                  </div>
                )}
              </VoiceLibrarySection>
            </div>
          </TabPane>
        </Tabs>
      </div>

      <VoiceDetailsModal
        visible={!!detailsView}
        voice={detailsView?.voice}
        variant={detailsView?.variant}
        displayName={detailsView?.displayName}
        onClose={() => setDetailsView(null)}
      />
    </Modal>
  )
}

export default VoiceSelectionModal
